import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import NavBar from '../components/navbar';
import AuthModal from '../components/AuthModal';
import { useAuth } from '../context/AuthContext';
import dataService from '../services/dataService';

const _etFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  year: 'numeric', month: 'short', day: 'numeric',
  hour: 'numeric', minute: '2-digit', hour12: true,
});
const formatET = (iso) => {
  if (!iso) return null;
  try { return _etFormatter.format(new Date(iso)) + ' ET'; } catch { return null; }
};

const PendingChanges = ({ onProfileClick, onBillingClick, onSeatsUpdate }) => {
  const { user, isImpersonating, canManageUsersTeams } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [error, setError] = useState(null);
  const [viewingTask, setViewingTask] = useState(null); // { task, record }
  const [expandedActionItems, setExpandedActionItems] = useState(new Set());
  const [expandedDiffs, setExpandedDiffs] = useState(new Set()); // set of task key strings

  // Team-scoped context passed via navigate(…, { state }) from CalendarConnection
  const scopedTeamName = location.state?.teamName || null;
  const scopedMemberEmails = location.state?.memberEmails || null;

  const isOwner = user?.role === 'owner';
  const canView = isOwner || canManageUsersTeams;
  // When impersonating an owner, scope pending-changes to that user's own account,
  // not the actual Account Owner's account (the JWT bearer).
  const targetEmail = (isImpersonating && isOwner) ? (user?.email ?? null) : null;

  const loadData = useCallback(async () => {
    if (!user || !canView) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      // Dedicated endpoint: returns only has_unpublished_changes=true records,
      // scoped to the caller's role (owner → all teams).
      // Records are NOT deduplicated by title — one per member who has pending changes.
      // targetEmail: during impersonation of an owner, scopes to that user's account.
      const result = await dataService.getPendingChanges(targetEmail);
      if (!result) {
        setError('Failed to load pending changes. Please try again.');
        return;
      }

      let pending = (result.tasks ?? []).filter(
        r => r.has_unpublished_changes === true && r.active !== false && !r.archive
      );

      // If navigated from CalendarConnection with a specific team scope,
      // filter down to that team's member emails.
      if (scopedMemberEmails && scopedMemberEmails.length > 0) {
        const scopedSet = new Set(scopedMemberEmails.map(e => e.toLowerCase()));
        pending = pending.filter(r => scopedSet.has((r.id || '').toLowerCase()));
      }

      setRecords(pending);
    } catch (err) {
      console.error('[PendingChanges] load error:', err);
      setError('Failed to load pending changes. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user, canView, targetEmail, scopedMemberEmails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  // Background polling: re-fetch every 30 s to pick up DynamoDB changes from any source.
  // Skips while the browser tab is hidden.
  useEffect(() => {
    if (!user || !canView) return;
    const INTERVAL = 30_000;
    let timerId = null;

    const poll = async () => {
      if (document.hidden) return;
      try {
        const result = await dataService.getPendingChanges(targetEmail);
        if (!result) return;
        let pending = (result.tasks ?? []).filter(
          r => r.has_unpublished_changes === true && r.active !== false && !r.archive
        );
        if (scopedMemberEmails && scopedMemberEmails.length > 0) {
          const scopedSet = new Set(scopedMemberEmails.map(e => e.toLowerCase()));
          pending = pending.filter(r => scopedSet.has((r.id || '').toLowerCase()));
        }
        setRecords(pending);
      } catch { /* network hiccup — try again next tick */ }
    };

    const onVisibility = () => {
      if (document.hidden) {
        clearInterval(timerId);
        timerId = null;
      } else {
        if (!timerId) timerId = setInterval(poll, INTERVAL);
      }
    };

    timerId = setInterval(poll, INTERVAL);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timerId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user, canView, targetEmail, scopedMemberEmails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setExpandedActionItems(new Set()); }, [viewingTask]);

  // Group records by task_list_title, sort groups by most-recent draft update
  const groups = useMemo(() => {
    const map = {};
    for (const record of records) {
      const title = record.task_list_title || 'Untitled';
      if (!map[title]) map[title] = [];
      map[title].push(record);
    }

    const latestTs = (rec) =>
      [...(rec.tasks || [])]
        .map(t => t.update_timestamp || '')
        .sort()
        .at(-1) || rec.last_edited_at || rec.published_at || '';

    return Object.entries(map)
      .map(([title, recs]) => {
        const sorted = [...recs].sort((a, b) => latestTs(b).localeCompare(latestTs(a)));
        return { title, records: sorted, latestTs: latestTs(sorted[0]) };
      })
      .sort((a, b) => b.latestTs.localeCompare(a.latestTs));
  }, [records]);

  const getUnpublishedTasks = (tasks, record) => {
    if (!tasks || tasks.length === 0) return [];
    const publishedAt = record.published_at ? new Date(record.published_at) : null;
    return [...tasks]
      .filter(t =>
        // Backend-annotated change (includes deleted ghosts with no update_timestamp)
        t._change_type
        // Fallback: timestamp-based detection when backend has no source record
        || (!!t.update_timestamp && (!publishedAt || new Date(t.update_timestamp) > publishedAt))
      )
      .sort((a, b) => {
        const aTs = a.update_timestamp || '';
        const bTs = b.update_timestamp || '';
        return bTs.localeCompare(aTs);
      });
  };

  const totalPending = records.reduce((s, r) => s + getUnpublishedTasks(r.tasks, r).length, 0);
  const uniqueMemberCount = new Set(
    records.filter(r => getUnpublishedTasks(r.tasks, r).length > 0).map(r => (r.id || '').toLowerCase()).filter(Boolean)
  ).size;

  // ── Render helpers ────────────────────────────────────────────────────────

  const CHANGE_CFG = {
    new:       { label: 'New',       bg: '#dcfce7', color: '#15803d', border: '#86efac', accentLeft: '#22c55e' },
    modified:  { label: 'Modified',  bg: '#fef9c3', color: '#92400e', border: '#fde68a', accentLeft: '#f59e0b' },
    deleted:   { label: 'Deleted',   bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5', accentLeft: '#ef4444' },
    completed: { label: 'Completed', bg: '#f0fdf4', color: '#166534', border: '#bbf7d0', accentLeft: '#22c55e' },
    reopened:  { label: 'Reopened',  bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe', accentLeft: '#3b82f6' },
  };

  const fmtShortDate = (iso) => {
    if (!iso) return 'none';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return iso; }
  };

  const renderTaskCard = (task, ti, record) => {
    const changeType    = task._change_type || null;
    const changedFields = task._changed_fields || [];
    const before        = task._before || {};
    const after         = task._after  || {};
    const cfg           = changeType ? CHANGE_CFG[changeType] : null;
    const isDeleted     = changeType === 'deleted';

    const today = new Date();
    const dueDate = task.due ? new Date(task.due) : null;
    const daysUntilDue = dueDate
      ? Math.round(
          (new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate()) -
           new Date(today.getFullYear(), today.getMonth(), today.getDate())) / 86400000
        )
      : null;
    const isCompleted = task.status === 'completed';
    const isOverdue   = !isCompleted && daysUntilDue !== null && daysUntilDue < 0;
    const hasDraft    = !!task.update_timestamp;

    const exactDate = dueDate
      ? dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : '';
    const dueColor = isCompleted ? '#22c55e'
      : !dueDate ? '#94a3b8'
      : isOverdue ? '#ef4444'
      : daysUntilDue === 0 ? '#f97316'
      : '#22c55e';

    // Field-level change summary lines
    const fieldSummary = [];
    if (changedFields.includes('title'))
      fieldSummary.push(`Title renamed`);
    if (changedFields.includes('due'))
      fieldSummary.push(`Due: ${fmtShortDate(before.due)} → ${fmtShortDate(after.due)}`);
    if (changedFields.includes('status'))
      fieldSummary.push(`Status: ${before.status || 'open'} → ${after.status || 'open'}`);
    if (changedFields.includes('action_items'))
      fieldSummary.push(`Subtasks: ${(before.action_items || []).length} → ${(after.action_items || []).length}`);

    const diffKey   = `${record.id}_${task.task_id || task.title || ti}`;
    const diffOpen  = expandedDiffs.has(diffKey);
    const hasDiff   = fieldSummary.length > 0;

    const cardBg     = cfg ? cfg.bg : (hasDraft ? '#fff7ed' : '#ffffff');
    const cardBorder = cfg ? cfg.border : (hasDraft ? '#fed7aa' : '#e2e8f0');
    const cardLeft   = cfg ? cfg.accentLeft : (hasDraft ? '#f97316' : '#cbd5e1');

    return (
      <div key={ti} style={{ marginBottom: 6 }}>
        <div
          onClick={() => isDeleted ? null : setViewingTask({ task, record })}
          onMouseEnter={e => { if (!isDeleted) { e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.13)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'; e.currentTarget.style.transform = 'translateY(0)'; }}
          style={{
            background: cardBg,
            borderRadius: hasDiff && diffOpen ? '8px 8px 0 0' : 8,
            padding: '9px 11px',
            border: `1px solid ${cardBorder}`,
            borderBottom: hasDiff && diffOpen ? `1px solid ${cardBorder}` : undefined,
            borderLeft: `3px solid ${cardLeft}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            cursor: isDeleted ? 'default' : 'pointer',
            transition: 'box-shadow 0.15s ease, transform 0.15s ease',
            opacity: isDeleted ? 0.75 : 1,
          }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Change type badge */}
              {cfg && (
                <span style={{
                  display: 'inline-block', fontSize: 9, fontWeight: 800,
                  padding: '1px 7px', borderRadius: 10, marginBottom: 4,
                  background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                  {cfg.label}
                </span>
              )}
              <div style={{
                fontSize: 13, fontWeight: 600, color: '#1e293b', lineHeight: '1.35', marginBottom: 2,
                textDecoration: isDeleted ? 'line-through' : 'none',
                opacity: isDeleted ? 0.65 : 1,
              }}>
                {task.title || 'Untitled task'}
              </div>
              {!isDeleted && dueDate && (
                <span style={{ fontSize: '12px', fontWeight: 700, color: dueColor }}>
                  {isCompleted ? `Due: ${exactDate} — Completed`
                    : isOverdue  ? `Due: ${exactDate} — Overdue ${Math.abs(daysUntilDue)}d`
                    : daysUntilDue === 0 ? `Due: ${exactDate} — Due today`
                    : `Due: ${exactDate} — Due in ${daysUntilDue}d`}
                </span>
              )}
              {hasDraft && !isDeleted && (
                <div style={{ fontSize: 11, color: '#92400e', marginTop: 3 }}>
                  <span style={{ color: '#b45309' }}>Updated:</span>{' '}
                  {formatET(task.update_timestamp) || task.update_timestamp}
                </div>
              )}
            </div>
            {/* Expand/collapse diff button */}
            {hasDiff && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  setExpandedDiffs(prev => {
                    const next = new Set(prev);
                    diffOpen ? next.delete(diffKey) : next.add(diffKey);
                    return next;
                  });
                }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 11, fontWeight: 700, color: cfg ? cfg.color : '#b45309',
                  padding: '2px 4px', borderRadius: 4, flexShrink: 0,
                  lineHeight: 1,
                }}
                title={diffOpen ? 'Hide changes' : 'Show changes'}
              >
                {diffOpen ? '▲' : '▼'}
              </button>
            )}
          </div>
          {!isDeleted && task.action_items?.length > 0 && (
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
              {task.action_items.filter(a => a.completed).length}/{task.action_items.length} subtasks
            </div>
          )}
          {/* Compact summary (always visible when there are changes) */}
          {fieldSummary.length > 0 && !diffOpen && (
            <div style={{ marginTop: 5, display: 'flex', flexWrap: 'wrap', gap: '4px 8px' }}>
              {fieldSummary.map((line, i) => (
                <span key={i} style={{ fontSize: 10, color: cfg ? cfg.color : '#92400e', fontWeight: 600 }}>
                  {line}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Expandable before/after panel */}
        {hasDiff && diffOpen && (
          <div style={{
            background: '#f8fafc', border: `1px solid ${cardBorder}`, borderTop: 'none',
            borderRadius: '0 0 8px 8px', padding: '8px 12px',
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
          }}>
            <div>
              <div style={{ fontSize: 9, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Before</div>
              {changedFields.map(f => (
                <div key={f} style={{ fontSize: 11, color: '#475569', marginBottom: 3 }}>
                  <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{f === 'action_items' ? 'Subtasks' : f}:</span>{' '}
                  {f === 'action_items'
                    ? `${(before.action_items || []).length} items`
                    : f === 'due' ? fmtShortDate(before[f])
                    : (before[f] ?? '—')}
                </div>
              ))}
            </div>
            <div>
              <div style={{ fontSize: 9, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>After</div>
              {changedFields.map(f => (
                <div key={f} style={{ fontSize: 11, color: '#1e293b', fontWeight: 600, marginBottom: 3 }}>
                  <span style={{ textTransform: 'capitalize' }}>{f === 'action_items' ? 'Subtasks' : f}:</span>{' '}
                  {f === 'action_items'
                    ? `${(after.action_items || []).length} items`
                    : f === 'due' ? fmtShortDate(after[f])
                    : (after[f] ?? '—')}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderUserSection = (record, ri, isLast) => {
    // display_name is enriched from record.id (the person who made the changes),
    // so it always represents the member/lead who has pending changes.
    const fromName = record.display_name || record.id || 'Unknown';

    const initial = (fromName[0] || '?').toUpperCase();

    const unpublishedTasks = getUnpublishedTasks(record.tasks, record);
    if (unpublishedTasks.length === 0) return null;

    return (
      <div key={ri} style={{ marginBottom: isLast ? 0 : 16 }}>
        {/* Per-user attribution row */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 10px 8px',
          borderBottom: '1px solid #dde3ea',
          marginBottom: 8,
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: 'linear-gradient(135deg, #f97316, #ea580c)',
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700, flexShrink: 0,
          }}>{initial}</div>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{fromName}</span>
        </div>
        {unpublishedTasks.map((task, ti) => renderTaskCard(task, ti, record))}
      </div>
    );
  };

  // ── Access guard ──────────────────────────────────────────────────────────

  if (!loading && !canView) {
    return (
      <div className="App">
        <NavBar onSignInClick={() => setShowAuthModal(true)} onProfileClick={onProfileClick} onBillingClick={onBillingClick} onSeatsUpdate={onSeatsUpdate} />
        <AuthModal showAuthModal={showAuthModal} setShowAuthModal={setShowAuthModal} />
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <p style={{ fontSize: 16, fontWeight: 500, color: '#64748b', margin: 0 }}>
            Pending changes are visible to Account Owners and Team Leads only.
          </p>
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <div className="App">
      <NavBar
        onSignInClick={() => setShowAuthModal(true)}
        onProfileClick={onProfileClick}
        onBillingClick={onBillingClick}
        onSeatsUpdate={onSeatsUpdate}
      />
      <AuthModal showAuthModal={showAuthModal} setShowAuthModal={setShowAuthModal} />

      {/* Banner — matches Tasks.js / CalendarConnection.js style */}
      <header className="App-header" style={{
        background: 'linear-gradient(180deg, #eef2f0 0%, #e4ece8 100%)',
        borderBottom: '1px solid #c8d8d2',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.07)',
        borderRadius: 0, margin: 0, padding: '0 20px',
        minHeight: 42, flexShrink: 0, position: 'relative',
      }}>
        {scopedTeamName && (
          <button
            onClick={() => navigate(-1)}
            style={{
              position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#4b6360', fontSize: 13, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '4px 8px', borderRadius: 6,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
          >
            ← Back
          </button>
        )}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 24, height: 24, borderRadius: 7,
              background: '#fef3c7', color: '#d97706',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14,
            }}>⏳</div>
            <h2 style={{ margin: 0, color: '#1e3a2f', fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>
              {scopedTeamName ? `Unshared Changes — ${scopedTeamName}` : 'Unshared Changes'}
            </h2>
          </div>
        </div>
      </header>

      <Container className="mt-2" style={{ position: 'relative', maxWidth: 1400 }}>
        {/* Loading */}
        {loading && (
          <>
            <style>{`@keyframes pc-spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.45)', borderRadius: 8, zIndex: 500, minHeight: 200 }} />
            <div style={{
              position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
              zIndex: 501, background: 'white', padding: '25px 35px', borderRadius: 12,
              display: 'flex', alignItems: 'center', gap: 15,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)', border: '2px solid #f97316', minWidth: 280,
            }}>
              <div style={{ width: 28, height: 28, border: '4px solid #f3f3f3', borderTop: '4px solid #f97316', borderRadius: '50%', animation: 'pc-spin 1s linear infinite' }} />
              <span style={{ fontSize: 17, fontWeight: 600, color: '#333' }}>Loading pending changes…</span>
            </div>
          </>
        )}

        {/* Error */}
        {!loading && error && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#ef4444' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
            <p style={{ fontSize: 15, fontWeight: 500, margin: '0 0 12px' }}>{error}</p>
            <button onClick={loadData} style={{ padding: '6px 18px', borderRadius: 6, border: 'none', background: '#1967d2', color: 'white', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              Retry
            </button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && groups.length === 0 && (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16, color: '#22c55e' }}>✓</div>
            <p style={{ fontSize: 17, fontWeight: 600, margin: '0 0 6px', color: '#374151' }}>
              {scopedTeamName ? `No pending changes for ${scopedTeamName}` : 'No pending changes'}
            </p>
            <p style={{ fontSize: 14, color: '#9ca3af', margin: 0 }}>
              {scopedTeamName
                ? 'There are no unshared task changes to review for this team.'
                : 'There are no unshared task changes across your teams.'}
            </p>
          </div>
        )}

        {/* Content */}
        {!loading && !error && groups.length > 0 && (
          <div style={{ padding: '14px 0 32px' }}>
            {/* Summary bar */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              marginBottom: 14, padding: '8px 14px',
              background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8,
            }}>
              <span style={{ fontSize: 18 }}>⏳</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#9a3412' }}>
                {totalPending} unshared change{totalPending !== 1 ? 's' : ''} from {uniqueMemberCount} member{uniqueMemberCount !== 1 ? 's' : ''} across{' '}
                {groups.length} task list{groups.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Groups — side-by-side columns, horizontally scrollable */}
            <div style={{ overflowX: 'auto', paddingBottom: '8px' }}>
              <div style={{ display: 'flex', gap: '18px', alignItems: 'flex-start', minWidth: 'max-content', padding: '4px 2px' }}>
                {groups.map(({ title, records: groupRecs }) => {
                  const groupTotal = groupRecs.reduce((s, r) => s + getUnpublishedTasks(r.tasks, r).length, 0);
                  if (groupTotal === 0) return null;
                  return (
                    <div key={title} style={{
                      width: 320, flexShrink: 0, borderRadius: 12, overflow: 'hidden',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.13)', border: '1px solid rgba(0,0,0,0.07)',
                    }}>
                      {/* Task list header bar */}
                      <div style={{
                        background: 'linear-gradient(145deg, #1a2540 0%, #243050 60%, #1e2d4a 100%)',
                        padding: '13px 16px',
                        display: 'flex', alignItems: 'center', gap: 10,
                      }}>
                        <span style={{ fontWeight: 700, fontSize: 14, color: '#fff', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
                        <span style={{
                          fontSize: 11, padding: '2px 10px', borderRadius: 10, fontWeight: 700, flexShrink: 0,
                          background: 'rgba(249,115,22,0.25)', color: '#fdba74', border: '1px solid rgba(249,115,22,0.35)',
                        }}>
                          {groupTotal} unshared
                        </span>
                      </div>

                      {/* Per-user sections */}
                      <div style={{
                        background: '#f1f4f8',
                        padding: '12px',
                      }}>
                        {groupRecs.map((record, ri) =>
                          renderUserSection(record, ri, ri === groupRecs.length - 1)
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </Container>
      {/* View-only task card modal */}
      {viewingTask && (() => {
        const vt           = viewingTask.task;
        const changeType   = vt._change_type || null;
        const changedFields = vt._changed_fields || [];
        const vtBefore     = vt._before || {};
        const vtAfter      = vt._after  || {};
        const cfg          = changeType ? CHANGE_CFG[changeType] : null;
        const isCompleted  = vt.status === 'completed';
        const statusColor  = isCompleted ? '#22c55e' : '#94a3b8';
        const statusBorder = isCompleted ? '#bbf7d0' : '#e2e8f0';
        const hasDraft     = !!vt.update_timestamp;
        const dueDate      = vt.due ? new Date(vt.due) : null;
        const exactDateLabel = dueDate
          ? dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : null;
        const listTitle = viewingTask.record?.task_list_title || '';
        const modalBorder = cfg ? cfg.accentLeft : (hasDraft ? '#f97316' : statusColor);

        return (
          <>
            {/* Backdrop */}
            <div onClick={() => setViewingTask(null)} style={{
              position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1000, backdropFilter: 'blur(2px)',
            }} />

            {/* Card */}
            <div style={{
              position: 'fixed', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1001, width: '460px', maxWidth: '94vw',
              backgroundColor: 'white', borderRadius: '14px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              border: `2px solid ${modalBorder}`,
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
              maxHeight: '88vh',
            }}>
              {/* Header */}
              <div style={{
                background: 'linear-gradient(145deg, #1a2540 0%, #243050 100%)',
                padding: '14px 18px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
              }}>
                <div style={{ fontWeight: 700, fontSize: '13px', color: '#fff', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{listTitle}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  {cfg && (
                    <span style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: 8,
                      background: cfg.bg, color: cfg.color,
                      border: `1px solid ${cfg.border}`, fontWeight: 800,
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                    }}>{cfg.label}</span>
                  )}
                  {hasDraft && !cfg && (
                    <span style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: 8,
                      background: 'rgba(249,115,22,0.25)', color: '#fdba74',
                      border: '1px solid rgba(249,115,22,0.35)', fontWeight: 700,
                    }}>⏳ Draft</span>
                  )}
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '14px', overflowY: 'auto' }}>
                {/* Title */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: 4, flexShrink: 0, marginTop: 2,
                    border: `2px solid ${statusColor}`,
                    background: isCompleted ? statusColor : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 800, color: isCompleted ? 'white' : '#d1d5db',
                  }}>✓</div>
                  <div style={{
                    flex: 1, fontWeight: 700, fontSize: '16px', color: '#1e293b',
                    lineHeight: '1.35',
                    textDecoration: isCompleted ? 'line-through' : 'none',
                    opacity: isCompleted ? 0.6 : 1,
                  }}>{vt.title || 'Untitled task'}</div>
                </div>

                {/* Due date */}
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>Due Date</div>
                  <div style={{
                    padding: '9px 12px', borderRadius: '8px',
                    border: `1.5px solid ${statusBorder}`, background: '#f8fafc',
                    fontSize: '13px', color: exactDateLabel ? '#1e293b' : '#94a3b8',
                  }}>{exactDateLabel || 'No due date'}</div>
                </div>

                {/* Updated timestamp */}
                {hasDraft && (
                  <div style={{ fontSize: 12, color: '#92400e' }}>
                    <span style={{ fontWeight: 600 }}>Updated: </span>
                    {formatET(vt.update_timestamp) || vt.update_timestamp}
                  </div>
                )}

                {/* Change diff panel */}
                {changedFields.length > 0 && (
                  <div style={{ borderRadius: 8, border: `1px solid ${cfg ? cfg.border : '#e2e8f0'}`, overflow: 'hidden' }}>
                    <div style={{ padding: '6px 12px', background: cfg ? cfg.bg : '#f8fafc', borderBottom: `1px solid ${cfg ? cfg.border : '#e2e8f0'}` }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: cfg ? cfg.color : '#64748b' }}>What changed</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: '#ffffff' }}>
                      <div style={{ padding: '10px 12px', borderRight: '1px solid #f1f5f9' }}>
                        <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Before</div>
                        {changedFields.map(f => (
                          <div key={f} style={{ fontSize: 12, color: '#64748b', marginBottom: 5 }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'capitalize', marginBottom: 1 }}>{f === 'action_items' ? 'Subtasks' : f}</div>
                            <div style={{ textDecoration: 'line-through', opacity: 0.8 }}>
                              {f === 'action_items' ? `${(vtBefore.action_items || []).length} items`
                                : f === 'due' ? fmtShortDate(vtBefore[f])
                                : (vtBefore[f] || '—')}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{ padding: '10px 12px' }}>
                        <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>After</div>
                        {changedFields.map(f => (
                          <div key={f} style={{ fontSize: 12, color: '#1e293b', marginBottom: 5 }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: '#64748b', textTransform: 'capitalize', marginBottom: 1 }}>{f === 'action_items' ? 'Subtasks' : f}</div>
                            <div style={{ fontWeight: 600 }}>
                              {f === 'action_items' ? `${(vtAfter.action_items || []).length} items`
                                : f === 'due' ? fmtShortDate(vtAfter[f])
                                : (vtAfter[f] || '—')}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Action items */}
                {(vt.action_items?.length > 0) && (
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>Action Items</div>
                    <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px 12px', border: `1px solid ${statusBorder}`, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {vt.action_items.map((item, i) => {
                        const expanded = expandedActionItems.has(i);
                        return (
                          <div
                            key={i}
                            onClick={() => setExpandedActionItems(prev => {
                              const next = new Set(prev);
                              expanded ? next.delete(i) : next.add(i);
                              return next;
                            })}
                            style={{ display: 'grid', gridTemplateColumns: '14px 1fr', gap: '6px', alignItems: expanded ? 'flex-start' : 'center', cursor: 'pointer', borderRadius: '4px', padding: '2px 4px', margin: '0 -4px', transition: 'background 0.1s' }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            <span style={{ color: statusColor, fontSize: '12px', fontWeight: 700, textAlign: 'center', marginTop: expanded ? '2px' : 0, userSelect: 'none' }}>{expanded ? '⌄' : '›'}</span>
                            <span style={{ fontSize: '12px', color: '#1e293b', whiteSpace: expanded ? 'normal' : 'nowrap', overflow: expanded ? 'visible' : 'hidden', textOverflow: expanded ? 'clip' : 'ellipsis' }}>{item}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{
                display: 'flex', justifyContent: 'flex-end',
                padding: '12px 20px', borderTop: '1px solid #f1f5f9', background: '#fafbfc',
              }}>
                <button onClick={() => setViewingTask(null)} style={{
                  padding: '8px 24px', borderRadius: '8px', border: '1.5px solid #e2e8f0',
                  background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px',
                  cursor: 'pointer',
                }}>Close</button>
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
};

export default PendingChanges;
